import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type CookieConsent = {
  necessary: boolean
  analytics: boolean
  performance: boolean
}

interface CookieConsentStore {
  consent: CookieConsent | null
  hasShownBanner: boolean
  setConsent: (consent: CookieConsent) => void
  acceptAll: () => void
  rejectAll: () => void
  resetConsent: () => void
}

const defaultConsent: CookieConsent = {
  necessary: true, // Always true - required for app functionality
  analytics: false,
  performance: false
}

export const useCookieConsentStore = create<CookieConsentStore>()(
  persist(
    (set, get) => ({
      consent: null,
      hasShownBanner: false,

      setConsent: (consent: CookieConsent) => {
        set({ 
          consent: { ...consent, necessary: true }, // Always keep necessary cookies enabled
          hasShownBanner: true 
        })
      },

      acceptAll: () => {
        const fullConsent: CookieConsent = {
          necessary: true,
          analytics: true,
          performance: true
        }
        set({ 
          consent: fullConsent,
          hasShownBanner: true 
        })
      },

      rejectAll: () => {
        set({ 
          consent: defaultConsent,
          hasShownBanner: true 
        })
      },

      resetConsent: () => {
        set({ 
          consent: null,
          hasShownBanner: false 
        })
      }
    }),
    {
      name: 'cookie-consent-storage',
      partialize: (state) => ({ 
        consent: state.consent,
        hasShownBanner: state.hasShownBanner 
      })
    }
  )
)

// Helper functions for checking consent
export const hasAnalyticsConsent = () => {
  const { consent } = useCookieConsentStore.getState()
  return consent?.analytics ?? false
}

export const hasPerformanceConsent = () => {
  const { consent } = useCookieConsentStore.getState()
  return consent?.performance ?? false
}

export const hasNecessaryConsent = () => {
  const { consent } = useCookieConsentStore.getState()
  return consent?.necessary ?? true // Default to true for necessary cookies
}
